import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductListing } from "@/components/storefront/ProductListing";
import { ChristmasStrip } from "@/components/storefront/ChristmasStrip";
import { CategoryIcon } from "@/components/storefront/CategoryIcon";
import { listCategories, listCategoryTopBrands } from "@/lib/products.functions";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { listingSearchSchema } from "@/lib/listing-search";
import { ChevronRight } from "lucide-react";
import { buildCategoryIconMap, categorySlug, parseCategoryId } from "@/lib/category-icon";
import { useMemo } from "react";
import { retryAsync } from "@/lib/ssr-retry";
import { getCategorySeoText } from "@/lib/category-seo";

export const Route = createFileRoute("/c/$slug")({
  validateSearch: listingSearchSchema,
  head: (ctx) => {
    const loaderData = (ctx as any).loaderData;
    const slug = (ctx as any).params?.slug ?? loaderData?.slug ?? "";
    const categories = (loaderData?.categories?.categories ?? []) as Cat[];
    const id = parseCategoryId(loaderData?.slug ?? "");
    const catByPsId = new Map(categories.map((c) => [c.ps_id, c]));
    const current = catByPsId.get(id);
    const name = current?.name ?? `Κατηγορία ${loaderData?.slug ?? ""}`;
    const url = `https://mrpanda.gr/c/${slug}`;
    const description = `Αγόρασε προϊόντα της κατηγορίας ${name} στο MrPanda.gr σε χαμηλές τιμές, με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα.`;
    // Category 2 is the PrestaShop root ("Home") category — not a real listing,
    // so keep it out of Google's index.
    const noindex = id === 2;
    return {
      meta: [
        { title: `${name} - MrPanda.gr` },
        { name: "description", content: description },
        ...(noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
        { property: "og:title", content: `${name} - MrPanda.gr` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${name} - MrPanda.gr`,
            description,
            url,
            inLanguage: "el",
          }),
        },
      ],
    };
  },
  loader: async ({ context, params }: any) => {
    let data: Awaited<ReturnType<typeof listCategories>> | null = null;
    try {
      data = await retryAsync(() =>
        context.queryClient.ensureQueryData({
          queryKey: ["categories"],
          queryFn: () => listCategories(),
          staleTime: 5 * 60_000,
        }),
      );
    } catch {
      // Στιγμιαία διακοπή upstream: η σελίδα φορτώνει και ζητά ξανά τα
      // δεδομένα στον browser, αντί να εμφανιστεί σελίδα σφάλματος.
      data = null;
    }
    return { categories: data, slug: params.slug };
  },
  component: CategoryPage,
});

type Cat = {
  id: string; ps_id: number; name: string;
  parent_ps_id: number | null; level_depth: number | null; position: number | null;
  icon_url?: string | null;
};

function CategoryPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const id = parseCategoryId(slug);
  const { data: catData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
    staleTime: 5 * 60_000,
  });

  const categories = (catData?.categories ?? []) as Cat[];
  useMemo(() => {
    if (categories.length > 0) buildCategoryIconMap(categories);
  }, [categories]);
  const catByPsId = new Map(categories.map((c) => [c.ps_id, c]));
  const childrenByPsId = new Map<number, Cat[]>();
  for (const c of categories) {
    if (c.parent_ps_id == null) continue;
    const arr = childrenByPsId.get(c.parent_ps_id) ?? [];
    arr.push(c);
    childrenByPsId.set(c.parent_ps_id, arr);
  }
  const collectDescendants = (rootId: number): number[] => {
    const out: number[] = [rootId];
    const stack = [rootId];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const ch of childrenByPsId.get(cur) ?? []) {
        out.push(ch.ps_id);
        stack.push(ch.ps_id);
      }
    }
    return out;
  };

  const current = catByPsId.get(id);
  const path: Cat[] = [];
  if (current) {
    let node: Cat | undefined = current;
    while (node) {
      path.unshift(node);
      node = node.parent_ps_id != null ? catByPsId.get(node.parent_ps_id) : undefined;
    }
  }

  const subcategories = categories
    .filter((c) => c.parent_ps_id === id)
    .map((c) => ({ ps_id: c.ps_id, name: c.name }));

  const resolveSubIds = (selected: number[] | undefined): number[] | undefined => {
    const roots = selected?.length ? selected : [id];
    const set = new Set<number>();
    for (const r of roots) for (const d of collectDescendants(r)) set.add(d);
    return Array.from(set);
  };

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-10 pt-6">
        {path.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            {path.map((c, i) => (
              <PathCrumb key={c.ps_id} psId={c.ps_id} name={c.name} sep={i < path.length - 1} />
            ))}
          </div>
        )}
        <CategoryHero
          psId={Number.isFinite(id) ? id : null}
          name={current?.name ?? `Κατηγορία ${slug}`}
          subcategories={subcategories}
          descendantIds={Number.isFinite(id) ? collectDescendants(id) : []}
          activeBrands={search.brands ?? []}
          onPickBrand={(brandPsId) =>
            navigate({
              search: (prev: typeof search) => ({
                ...prev,
                brands: prev.brands?.includes(brandPsId) ? undefined : [brandPsId],
                page: 1,
              }),
            })
          }
        />
        <ChristmasStrip className="px-0 pb-4 pt-4" />
        {Number.isFinite(id) && (
          <ProductListing
            scope={{
              mode: "category",
              categoryPsId: id,
              subcategories,
              resolveSubIds,
              cacheKey: `cat:${id}:${categories.length}`,
            }}
            search={search}
            onChange={(next) => navigate({ search: (prev: typeof search) => ({ ...prev, ...next }), replace: true })}
          />
        )}
        <CategorySeoText name={current?.name ?? null} />
      </div>

    </StorefrontLayout>
  );
}

function PathCrumb({ psId, name, sep }: { psId: number; name: string; sep: boolean }) {
  return (
    <span className="flex items-center gap-1">
      <Link to="/c/$slug" params={{ slug: categorySlug(psId, name) }} className="inline-flex items-center gap-1 hover:text-foreground hover:underline">
        <CategoryIcon psId={psId} className="h-4 w-4" />
        {name}
      </Link>
      {sep && <ChevronRight className="h-3 w-3" />}
    </span>
  );
}

function CategoryHero({
  psId,
  name,
  subcategories,
  descendantIds,
  activeBrands,
  onPickBrand,
}: {
  psId: number | null;
  name: string;
  subcategories: { ps_id: number; name: string }[];
  descendantIds: number[];
  activeBrands: number[];
  onPickBrand: (brandPsId: number) => void;
}) {
  const doodles = ["✨", "🎈", "🛍️", "💛", "🐾", "🎀", "🌟"];
  const { data: brandsData } = useQuery({
    queryKey: ["cat-brands", psId, descendantIds.length],
    queryFn: () => listCategoryTopBrands({ data: { categoryPsIds: descendantIds, limit: 5 } }),
    enabled: descendantIds.length > 0,
    staleTime: 10 * 60_000,
  });
  const topBrands = brandsData?.brands ?? [];
  return (
    <div className="relative mb-4 animate-pop-in">
      {/* playful floating doodles around the open header */}
      {doodles.map((d, i) => (
        <span
          key={i}
          aria-hidden
          className="pointer-events-none absolute -z-10 select-none text-2xl opacity-25 animate-float-y sm:text-3xl"
          style={{
            top: `${-4 + ((i * 23) % 18)}%`,
            left: `${4 + ((i * 37) % 88)}%`,
            animationDelay: `${i * 0.28}s`,
          }}
        >
          {d}
        </span>
      ))}

      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        {/* left: icon + title cluster */}
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="grid h-28 w-28 shrink-0 place-items-center rounded-3xl bg-gradient-to-br from-primary/25 to-accent/50 p-3 shadow-sm ring-1 ring-primary/10 backdrop-blur animate-bob sm:h-44 sm:w-44">
            <CategoryIcon psId={psId} className="h-full w-full" width={400} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-4xl font-black leading-none tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {name}
            </h1>
            <p className="mt-2 text-base font-medium text-muted-foreground sm:text-lg">
              Ξετρύπωσε τις καλύτερες επιλογές μας 🎉
            </p>
          </div>
        </div>

        {/* right: floating brand bubbles */}
        {topBrands.length > 0 && (
          <div className="relative z-10 flex max-w-full flex-wrap items-center gap-3 sm:gap-4 lg:flex-nowrap lg:justify-end">
            {topBrands.map((b, i) => {
              const active = activeBrands.includes(b.ps_id);
              const offsets = [-8, 10, -5, 12, 0, 6];
              const tilts = [-5, 5, -3, 6, -4, 4];
              return (
                <button
                  key={b.ps_id}
                  type="button"
                  onClick={() => onPickBrand(b.ps_id)}
                  title={`${b.name} — ${b.productCount} προϊόντα`}
                  aria-pressed={active}
                  className={`animate-float-y group inline-flex h-16 w-[132px] shrink-0 items-center justify-center rounded-[28px] border-2 border-dashed bg-white/80 px-3 py-2 shadow-sm transition-transform hover:-translate-y-1 hover:bg-white ${
                    active
                      ? "border-primary ring-2 ring-primary/40"
                      : "border-primary/40 hover:border-primary"
                  }`}
                  style={{
                    animationDelay: `${i * 0.31}s`,
                    marginTop: `${offsets[i % offsets.length]}px`,
                    rotate: `${tilts[i % tilts.length]}deg`,
                  }}
                >
                  {b.logo_url ? (
                    <BrandLogo
                      logoUrl={b.logo_url}
                      name={b.name}
                      width={96}
                      className="h-full w-full overflow-hidden rounded-[18px] border-0 bg-transparent"
                      imgClassName="max-h-[44px] max-w-[112px] object-contain"
                    />
                  ) : (
                    <span className="line-clamp-2 text-center text-xs font-extrabold leading-tight text-foreground">
                      {b.name}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {subcategories.length > 0 && (
        <div className="relative mt-6 flex flex-wrap gap-2">
          {subcategories.map((s) => (
            <Link
              key={s.ps_id}
              to="/c/$slug"
              params={{ slug: categorySlug(s.ps_id, s.name) }}
              className="mp-bouncy inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm shadow-sm transition-colors hover:border-primary hover:text-primary"
            >
              <CategoryIcon psId={s.ps_id} className="h-4 w-4" />
              {s.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CategorySeoText({ name }: { name: string | null }) {
  const seo = getCategorySeoText(name);
  return (
    <section className="mt-10 rounded-3xl border border-border bg-muted/30 p-6 text-sm leading-relaxed text-muted-foreground sm:p-8">
      <h2 className="mb-3 text-lg font-bold text-foreground">{seo.title}</h2>
      {seo.body.map((p: string, i: number) => (
        <p key={i} className={i > 0 ? "mt-3" : undefined}>
          {p}
        </p>
      ))}
    </section>
  );
}
